import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { accountEndpoints, fantasyEndpoints } from '@pff-consumer/api-sdk'
import { envConfig, formatUpdatedTime } from '@pff-consumer/utils'
import {
  NextPickInfoSuggestionsTransformedResponse,
  NextPickInfo,
  NextPickInfoSuggestionsApiResponse,
  InitPlayersResponse,
  InitPlayersRequest,
  GetNextPickInfoRequest,
  GetNextCpuPicksRequest,
  GetNextCpuPicksResponse,
  League,
  MDSResultResponse,
  GetMdsResultsRequest,
  GetMdsResultsResponse,
  LiveDraftPick,
  Drafter,
  LiveDraftStatus,
  HostedSites,
  HAS_SEEN_RESYNC_LEAGUES_MODAL,
  type FantasyMatchupPosition,
  type RunningBackType,
  type UpdatedTimeVariant,
} from '@pff-consumer/schema'
import AsyncStorage from '@react-native-async-storage/async-storage'
import type { Keeper } from '@pff-consumer/schema/fantasy-live-draft/keeper'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { fantasySlice } from '../fantasy/fantasy.slice'
import { transformNextPickInfoApiResponse } from './utils/transform-next-pick-info-api-response'
import { getPlayerPickGradeMap } from './utils/get-player-pick-grade-map'
import { transformMdsResultApiResponse } from './utils/transform-mds-result-api-response'
import { getPlayersNextRoundAvailability } from './utils/get-players-next-round-availability'
import { isHydrateAction } from '../helpers/hydrateAction'

const { CONSUMER_API_MIDDLEWARE_URL, CONSUMER_API_KEY } = envConfig

const getAuthTokenFromState = (state: unknown): string | undefined => {
  // @ts-ignore
  return state?.account?.token
}

const baseUrl = process.env.NODE_ENV === 'development' ? `${CONSUMER_API_MIDDLEWARE_URL}/` : '/'

const DEFAULT_MDS_PICK_NUMBER = 1
export const fantasyLdaMdsApi = createApi({
  reducerPath: 'fantasyLdaMdsApi',
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      // We need to get the auth/jwt token from the store and add to the headers for every api call
      const authToken = getAuthTokenFromState(getState())
      if (authToken) {
        headers.append('Authorization', authToken)
      }
      headers.append('Api-Key', CONSUMER_API_KEY)

      return headers
    },
  }),
  extractRehydrationInfo(action, { reducerPath }) {
    if (isHydrateAction(action)) {
      return action.payload[reducerPath]
    }
  },
  endpoints: (builder) => ({
    getToken: builder.mutation<{ token: string }, {}>({
      query: (body) => ({
        url: `${baseUrl}${accountEndpoints.getToken().url}`,
        method: 'POST',
        body,
      }),
    }),
    initialize: builder.query<InitPlayersResponse, { body: InitPlayersRequest }>({
      query: ({ body }) => ({
        url: `${baseUrl}${fantasyEndpoints.mds.initialize()}`,
        method: 'POST',
        body,
      }),
    }),
    getNextPickInfo: builder.query<NextPickInfoSuggestionsTransformedResponse, { body: GetNextPickInfoRequest }>({
      query: ({ body }) => ({
        url: `${baseUrl}${fantasyEndpoints.mds.getNextPickInfo()}`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: NextPickInfoSuggestionsApiResponse) => {
        const { nextPickInfo, suggestions, suggestedNextPicks } = response
        const requiredSuggestions: NextPickInfo[] = transformNextPickInfoApiResponse(suggestions, suggestedNextPicks)
        const playerPickGrades = getPlayerPickGradeMap(suggestions)
        const playersNextRoundAvailability = getPlayersNextRoundAvailability(suggestedNextPicks)

        return {
          pickInfo: nextPickInfo,
          suggestions: { round: nextPickInfo?.round, suggestions: requiredSuggestions },
          pickGrades: playerPickGrades,
          nextRoundAvailability: playersNextRoundAvailability,
        }
      },
    }),
    getNextCpuPicks: builder.query<GetNextCpuPicksResponse, { body: GetNextCpuPicksRequest }>({
      query: ({ body }) => ({
        url: `${baseUrl}${fantasyEndpoints.mds.getNextCpuPicks()}`,
        method: 'POST',
        body,
      }),
    }),
    getMDSLDASettingsData: builder.query<
      {
        data: {
          league: League | null
          drafters: Drafter[]
          keepers: Keeper[]
        }
      },
      { useYahooExtensionFlow?: boolean; season: number }
    >({
      // @ts-expect-error
      async queryFn(arg, listenerApi, _extraOptions, fetchWithBQ) {
        const state = listenerApi.getState()

        // @ts-expect-error
        const currentlySelectedSyncedLeague: League | null = state.fantasy.draftRoom.selectedSyncedLeague
        // @ts-expect-error
        const userAlreadyHasLeagues = state.fantasy.draftRoom.syncedLeagues?.length > 0
        let drafters: Drafter[] = []
        let keepers: Keeper[] = []

        // @ts-expect-error
        const userLeaguesResponse: { data: { leagues: League[] }; error?: FetchBaseQueryError | null } =
          await fetchWithBQ(fantasyEndpoints.getMyLeagues({ season: arg.season }))

        if (userLeaguesResponse.error) {
          return { error: userLeaguesResponse.error as FetchBaseQueryError }
        }
        listenerApi.dispatch(fantasySlice.actions.updateSyncedLeagues(userLeaguesResponse.data?.leagues))

        // If user has no leagues, set synced league to null and bail out early
        if (userLeaguesResponse.data?.leagues?.length === 0) {
          listenerApi.dispatch(fantasySlice.actions.updateSyncedLeague(null))

          return {
            data: {
              league: null,
              drafters,
              keepers,
            },
          }
        }

        let leagueToUse: League | null = currentlySelectedSyncedLeague
        // If there is no currently selected league, auto-select the first league
        if (!currentlySelectedSyncedLeague && !userAlreadyHasLeagues) {
          const leagueToAutoSelect = userLeaguesResponse.data?.leagues[0]
          // TODO more bespoke logic around leagues data
          // Auto-select the users synced league
          listenerApi.dispatch(fantasySlice.actions.updateSyncedLeague(leagueToAutoSelect))
          leagueToUse = leagueToAutoSelect
        }

        // If we are in LDA mode and using Yahoo then do not get keepers or drafters
        if (arg.useYahooExtensionFlow) {
          return {
            data: {
              league: leagueToUse,
              drafters,
              keepers,
            },
          }
        }

        if (leagueToUse?.hosted_site === HostedSites.NONE) {
          return {
            data: {
              league: leagueToUse,
              drafters,
              keepers,
            },
          }
        }

        // @ts-expect-error
        const leagueDraftersResponse: { data: { drafters: Drafter[] }; error?: FetchBaseQueryError | null } =
          await fetchWithBQ(fantasyEndpoints.getLeagueDrafters({ leagueId: leagueToUse?.id || -1, season: arg.season }))

        // Handle the user's drafters + user pick
        if (leagueDraftersResponse.data?.drafters?.length > 0) {
          drafters = leagueDraftersResponse.data?.drafters
          listenerApi.dispatch(fantasySlice.actions.updateLeagueDrafters(drafters))

          // Find user pick
          const userPick = drafters.find(
            (drafter) => drafter.drafteeName.toLowerCase() === leagueToUse?.team_name.toLowerCase()
          )

          if (userPick && userPick?.pickNumber) {
            listenerApi.dispatch(fantasySlice.actions.updateDraftRoomSettingsUserPick(userPick.pickNumber))
          }
        } else {
          listenerApi.dispatch(fantasySlice.actions.updateLeagueDrafters([]))
          listenerApi.dispatch(fantasySlice.actions.updateDraftRoomSettingsUserPick(DEFAULT_MDS_PICK_NUMBER))
        }

        // Only get keepers if league is NOT on Yahoo
        if (leagueToUse?.hosted_site !== HostedSites.YAHOO) {
          // @ts-expect-error
          const leagueKeepersResponse: { data: { keepers: Keeper[] }; error?: FetchBaseQueryError | null } =
            await fetchWithBQ(fantasyEndpoints.getLeagueKeepers({ leagueId: leagueToUse?.id || -1 }))

          if (leagueKeepersResponse?.data?.keepers?.length > 0) {
            keepers = leagueKeepersResponse.data?.keepers
            listenerApi.dispatch(fantasySlice.actions.updateLeagueKeepers(keepers))
          } else {
            listenerApi.dispatch(fantasySlice.actions.updateLeagueKeepers([]))
          }
        } else {
          listenerApi.dispatch(fantasySlice.actions.updateLeagueKeepers([]))
        }

        return {
          data: {
            league: leagueToUse,
            drafters,
            keepers,
          },
        }
      },
    }),
    getMdsResults: builder.query<GetMdsResultsResponse, { body: GetMdsResultsRequest }>({
      query: ({ body }) => ({
        url: `${baseUrl}${fantasyEndpoints.mds.getResults()}`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: MDSResultResponse) => {
        const draftedTeamsResult = transformMdsResultApiResponse(response)
        return { userTeamId: response.user_picks_with_grades.fantasy_team[0], draftedTeamsResult }
      },
    }),
    getUpdatedTime: builder.query<
      { lastUpdatedAt: string | null },
      {
        variant: UpdatedTimeVariant
        matchupPosition?: FantasyMatchupPosition
        matchupRunningBackType?: RunningBackType
      }
    >({
      query: ({ variant, matchupPosition, matchupRunningBackType }) => ({
        url: `${baseUrl}${fantasyEndpoints.mds.getUpdatedTime({ variant })}`,
        method: 'GET',
        params: {
          matchupPosition,
          matchupRunningBackType,
        },
      }),
      transformResponse: (response: { lastUpdatedAt: number }) => {
        const updatedAtTime = formatUpdatedTime(response.lastUpdatedAt)
        return { lastUpdatedAt: updatedAtTime }
      },
    }),
    getLeagueDraftPicks: builder.query<
      { picks: LiveDraftPick[]; status: LiveDraftStatus } & { authorizeUrl: string },
      { leagueId: number }
    >({
      query: fantasyEndpoints.getLeagueDraftPicks,
    }),
    getLeagueDrafters: builder.query<{ drafters: Drafter[] }, { leagueId: number }>({
      query: fantasyEndpoints.getLeagueDrafters,
      keepUnusedDataFor: 5,
    }),
    getLeagueKeepers: builder.query<{ keepers: Keeper[] }, { leagueId: number }>({
      query: fantasyEndpoints.getLeagueKeepers,
      keepUnusedDataFor: 5,
    }),
    showResyncLeaguesModal: builder.query<boolean, { season: number }>({
      async queryFn(arg, listenerApi, _extraOptions, fetchWithBQ) {
        const hasSeen = await AsyncStorage.getItem(HAS_SEEN_RESYNC_LEAGUES_MODAL)

        if (!hasSeen || hasSeen === 'false') {
          AsyncStorage.setItem(HAS_SEEN_RESYNC_LEAGUES_MODAL, 'false')

          // @ts-expect-error
          const response: { data: boolean; error?: FetchBaseQueryError | null } = await fetchWithBQ(
            fantasyEndpoints.showResyncLeaguesModal({ season: arg.season })
          )

          if (response.error) {
            return { error: response.error }
          }

          return { data: response.data }
        }

        return { data: false }
      },
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetTokenMutation,
  useGetNextPickInfoQuery,
  useGetMdsResultsQuery,
  useGetUpdatedTimeQuery,
  useGetLeagueDraftPicksQuery,
  useGetLeagueDraftersQuery,
  useGetLeagueKeepersQuery,
  useGetMDSLDASettingsDataQuery,
  useShowResyncLeaguesModalQuery,
} = fantasyLdaMdsApi

// Export getters to be used when rendering components on the server
export const {
  getToken,
  getNextPickInfo,
  getMdsResults,
  getUpdatedTime,
  getLeagueDraftPicks,
  getLeagueDrafters,
  getLeagueKeepers,
  getMDSLDASettingsData,
  showResyncLeaguesModal,
} = fantasyLdaMdsApi.endpoints
